import { useState, useEffect, lazy, Suspense, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/auth.store';
import { useNotificationsStore } from '../store/notifications.store';
import { authApi, billingApi, tenantSettingsApi, privacyApi } from '../services/api';
import DriverPrivacySection from '../components/DriverPrivacySection';
import WorkScheduleEditor, {
  DEFAULT_WORK_SCHEDULE,
  hasSomeWork,
  privacySummary,
  type WorkSchedule,
} from '../components/WorkScheduleEditor';
import PushNotificationToggle from '../components/PushNotificationToggle';
import { InstallPrompt } from '../components/InstallPrompt';
import { useInstallPrompt } from '../hooks/useInstallPrompt';
import { DriverModeToggle } from '../components/DriverModeToggle';
import {
  User,
  Users,
  Bell,
  Shield,
  Building,
  Save,
  Check,
  AlertCircle,
  Eye,
  EyeOff,
  Volume2,
  VolumeX,
  Loader2,
  Mail,
  Smartphone,
  Globe,
  Palette,
  Battery,
  Car,
  Key,
  QrCode,
  ShieldCheck,
  ShieldOff,
  Copy,
  Map,
  Layers,
  Sun,
  Moon,
  Monitor,
  Phone,
  CreditCard,
  MessageCircle,
  FolderTree,
  Fingerprint,
  Pencil,
  Trash2,
} from 'lucide-react';
import clsx from 'clsx';
import { browserSupportsWebAuthn, registerPasskey } from '../services/webauthn';
import { useSettingsStore, MAP_STYLES, THEME_OPTIONS } from '../store/settings.store';
import { useTenantContextStore } from '../store/tenant-context.store';

// Lazy-loaded page components for admin tabs
const ApiKeysPage = lazy(() => import('./ApiKeysPage'));
const BrandingPage = lazy(() => import('./BrandingPage'));
const UsersPage = lazy(() => import('./UsersPage'));
const GroupsPage = lazy(() => import('./GroupsPage'));
const BillingPage = lazy(() => import('./BillingPage'));
const TenantRolesPage = lazy(() => import('./TenantRolesPage'));

type TabId = 'profile' | 'notifications' | 'security' | 'display' | 'app' | 'privacy'
  | 'api-keys' | 'branding' | 'users' | 'roles' | 'groups' | 'billing' | 'tenant';

interface TabDef {
  id: TabId;
  label: string;
  icon: React.ElementType;
  adminOnly?: boolean;
  /**
   * Shown to admins (who set the company working hours) and to anyone assigned
   * a vehicle (who declares their own leave). Gated on assignment rather than
   * on role: a manager with a company car has the same right to declare leave
   * as a DRIVER, and a DRIVER with no vehicle has nothing to declare.
   */
  privacyTab?: boolean;
}

// `label` holds an i18n key resolved with t() at render time.
const allTabs: TabDef[] = [
  { id: 'profile', label: 'settings.tab_profile', icon: User },
  { id: 'notifications', label: 'settings.tab_notifications', icon: Bell },
  { id: 'display', label: 'settings.tab_display', icon: Map },
  { id: 'app', label: 'settings.tab_app', icon: Smartphone },
  { id: 'security', label: 'settings.tab_security', icon: Shield },
  { id: 'privacy', label: 'settings.tab_privacy', icon: Shield, privacyTab: true },
  { id: 'api-keys', label: 'settings.tab_api_keys', icon: Key, adminOnly: true },
  { id: 'branding', label: 'settings.tab_branding', icon: Palette, adminOnly: true },
  { id: 'users', label: 'settings.tab_users', icon: Users, adminOnly: true },
  { id: 'roles', label: 'settings.tab_roles', icon: Shield, adminOnly: true },
  { id: 'groups', label: 'settings.tab_groups', icon: FolderTree, adminOnly: true },
  { id: 'billing', label: 'settings.tab_billing', icon: CreditCard, adminOnly: true },
  { id: 'tenant', label: 'settings.tab_tenant', icon: Building },
];

function TabFallback() {
  return (
    <div className="flex items-center justify-center py-16">
      <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
    </div>
  );
}

export default function SettingsPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab') as TabId | null;
  const [activeTab, setActiveTab] = useState<TabId>(requestedTab ?? 'profile');
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN';

  // Shares its cache entry with PrivacyBanner, so showing the tab costs no
  // extra request.
  const { data: myPrivacyDevices } = useQuery({
    queryKey: ['my-privacy'],
    queryFn: privacyApi.getMine,
    retry: false,
  });
  const hasOwnVehicle = (myPrivacyDevices?.length ?? 0) > 0;

  const tabs = useMemo(
    () =>
      allTabs.filter(
        (t) =>
          (!t.adminOnly || isAdmin) &&
          // Drivers always see it, even with no vehicle assigned: hiding the tab
          // also hid the explanation of why it is empty, leaving them staring at
          // a feature they were told about with no way to find out what happened.
          (!t.privacyTab || isAdmin || hasOwnVehicle || user?.role === 'DRIVER')
      ),
    [isAdmin, hasOwnVehicle, user?.role]
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('settings.title')}</h1>
        <p className="text-gray-500 mt-1">{t('settings.subtitle')}</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Tabs - horizontal scroll on mobile, vertical list on desktop */}
        <div className="lg:w-56 flex-shrink-0">
          <nav className="glass-card p-2 lg:p-2">
            <div className="flex lg:flex-col gap-1 overflow-x-auto pb-1 lg:pb-0 scrollbar-hide">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={clsx(
                    'flex items-center justify-center rounded-xl transition-all flex-shrink-0',
                    'flex-col p-2 lg:flex-row lg:justify-start lg:gap-3 lg:px-4 lg:py-3',
                    activeTab === tab.id
                      ? 'bg-primary-50 text-primary-700 border border-primary-200'
                      : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700 lg:text-gray-600 lg:hover:text-gray-900'
                  )}
                >
                  <tab.icon className={clsx('h-5 w-5', activeTab === tab.id ? 'text-primary-600' : '')} />
                  <span className="text-[10px] font-medium leading-tight text-center mt-1 lg:mt-0 lg:text-sm lg:text-left whitespace-nowrap">
                    {t(tab.label)}
                  </span>
                </button>
              ))}
            </div>
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 glass-card p-6">
          {activeTab === 'profile' && <ProfileSettings />}
          {activeTab === 'notifications' && <NotificationSettings />}
          {activeTab === 'display' && <DisplaySettings />}
          {activeTab === 'app' && <AppSettings />}
          {activeTab === 'security' && <SecuritySettings />}
          {activeTab === 'privacy' && (
            <div className="space-y-6">
              {/* Company-wide working hours first: they are what every vehicle
                  inherits, so a driver's own leave reads as an exception to them. */}
              <TenantPrivacyDefaults />
              <DriverPrivacySection />
            </div>
          )}
          {activeTab === 'api-keys' && <Suspense fallback={<TabFallback />}><ApiKeysPage /></Suspense>}
          {activeTab === 'branding' && <Suspense fallback={<TabFallback />}><BrandingPage /></Suspense>}
          {activeTab === 'users' && <Suspense fallback={<TabFallback />}><UsersPage /></Suspense>}
          {activeTab === 'roles' && <Suspense fallback={<TabFallback />}><TenantRolesPage /></Suspense>}
          {activeTab === 'groups' && <Suspense fallback={<TabFallback />}><GroupsPage /></Suspense>}
          {activeTab === 'billing' && <Suspense fallback={<TabFallback />}><BillingPage /></Suspense>}
          {/* Privacy defaults used to live here, buried under the whole
              Organisation form — moved to the dedicated "Vie privée" tab. */}
          {activeTab === 'tenant' && <TenantSettings />}
        </div>
      </div>
    </div>
  );
}

function ProfileSettings() {
  const { t } = useTranslation();
  const { user, setUser } = useAuthStore();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phone: user?.phone || '',
  });

  const updateMutation = useMutation({
    mutationFn: (data: { firstName: string; lastName: string; phone?: string | null }) => authApi.updateProfile(data),
    onSuccess: () => {
      if (user) {
        setUser({
          ...user,
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone || undefined,
        });
      }
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
  });

  const handleSave = () => {
    updateMutation.mutate({
      firstName: formData.firstName,
      lastName: formData.lastName,
      phone: formData.phone || null,
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">{t('settings.section_profile')}</h2>
        <p className="text-sm text-gray-500">{t('settings.section_profile_subtitle')}</p>
      </div>

      <div className="space-y-6">
        {/* Avatar */}
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center text-white text-2xl font-bold shadow-glow">
            {user?.firstName?.[0]}
            {user?.lastName?.[0]}
          </div>
          <div>
            <p className="text-lg font-medium text-gray-900">
              {user?.firstName} {user?.lastName}
            </p>
            <p className="text-sm text-gray-500">{user?.email}</p>
            <p className="text-xs text-gray-400 mt-1">
              Role: <span className="text-primary-600">{user?.role}</span>
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2">{t('settings.field_first_name')}</label>
            <input
              type="text"
              value={formData.firstName}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 transition-colors"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2">{t('settings.field_last_name')}</label>
            <input
              type="text"
              value={formData.lastName}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 transition-colors"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2">{t('settings.field_email')}</label>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="email"
                value={formData.email}
                className="w-full pl-11 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-500 cursor-not-allowed"
                disabled
              />
            </div>
            <p className="mt-1.5 text-xs text-gray-400">{t('settings.email_locked_help')}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2">{t('settings.field_phone')}</label>
            <div className="relative">
              <Phone className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+33 6 12 34 56 78"
                className="w-full pl-11 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 transition-colors"
              />
            </div>
            <p className="mt-1.5 text-xs text-gray-400">{t('settings.phone_required_sms_help')}</p>
          </div>
        </div>

        <div className="flex items-center gap-3 pt-4">
          <button
            onClick={handleSave}
            disabled={updateMutation.isPending}
            className="inline-flex items-center px-5 py-2.5 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-xl hover:opacity-90 disabled:opacity-50 transition-opacity"
          >
            {updateMutation.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {t('settings.save_btn')}
          </button>
          {updateMutation.isSuccess && (
            <span className="inline-flex items-center text-sm text-green-400">
              <Check className="h-4 w-4 mr-1" />
              {t('settings.saved')}
            </span>
          )}
          {updateMutation.isError && (
            <span className="inline-flex items-center text-sm text-red-400">
              <AlertCircle className="h-4 w-4 mr-1" />
              {t('settings.save_error')}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function NotificationSettings() {
  const { t } = useTranslation();
  const { user, setUser } = useAuthStore();
  const { soundEnabled, toggleSound } = useNotificationsStore();
  const queryClient = useQueryClient();

  const [settings, setSettings] = useState({
    notifyEmail: user?.notifyEmail ?? true,
    notifyPush: user?.notifyPush ?? true,
    notifySms: user?.notifySms ?? false,
    notifyWhatsapp: user?.notifyWhatsapp ?? false,
  });

  // Update local state when user changes
  useEffect(() => {
    if (user) {
      setSettings({
        notifyEmail: user.notifyEmail ?? true,
        notifyPush: user.notifyPush ?? true,
        notifySms: user.notifySms ?? false,
        notifyWhatsapp: user.notifyWhatsapp ?? false,
      });
    }
  }, [user]);

  const updateMutation = useMutation({
    mutationFn: (data: { notifyEmail: boolean; notifyPush: boolean; notifySms: boolean; notifyWhatsapp: boolean }) =>
      authApi.updateProfile(data),
    onSuccess: () => {
      if (user) {
        setUser({
          ...user,
          notifyEmail: settings.notifyEmail,
          notifyPush: settings.notifyPush,
          notifySms: settings.notifySms,
          notifyWhatsapp: settings.notifyWhatsapp,
        });
      }
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
  });

  const handleToggle = (key: keyof typeof settings) => {
    setSettings((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSave = () => {
    updateMutation.mutate(settings);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">{t('settings.section_notifications')}</h2>
        <p className="text-sm text-gray-500">{t('settings.notif_subtitle')}</p>
      </div>

      <div className="space-y-6">
        {/* Sound */}
        <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {soundEnabled ? (
                <Volume2 className="h-5 w-5 text-primary-600" />
              ) : (
                <VolumeX className="h-5 w-5 text-gray-400" />
              )}
              <div>
                <p className="font-medium text-gray-900">{t('settings.notif_sound_title')}</p>
                <p className="text-xs text-gray-500">{t('settings.notif_sound_desc')}</p>
              </div>
            </div>
            <ToggleSwitch checked={soundEnabled} onChange={toggleSound} />
          </div>
        </div>

        {/* Channels */}
        <div>
          <h3 className="text-sm font-medium text-gray-600 mb-3">{t('settings.notif_channels_section')}</h3>
          <div className="space-y-3">
            <ToggleItem
              icon={Mail}
              label={t('settings.notif_email_label')}
              description={t('settings.notif_email_desc')}
              checked={settings.notifyEmail}
              onChange={() => handleToggle('notifyEmail')}
            />
            <ToggleItem
              icon={Bell}
              label={t('settings.notif_push_label')}
              description={t('settings.notif_push_desc')}
              checked={settings.notifyPush}
              onChange={() => handleToggle('notifyPush')}
            />
            <ToggleItem
              icon={Phone}
              label={t('settings.notif_sms_label')}
              description={t('settings.notif_sms_desc')}
              checked={settings.notifySms}
              onChange={() => handleToggle('notifySms')}
            />
            <ToggleItem
              icon={MessageCircle}
              label={t('settings.notif_whatsapp_label')}
              description={t('settings.notif_whatsapp_desc')}
              checked={settings.notifyWhatsapp}
              onChange={() => handleToggle('notifyWhatsapp')}
            />
          </div>
          {(settings.notifySms || settings.notifyWhatsapp) && !user?.phone && (
            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-700">
                  {settings.notifySms && settings.notifyWhatsapp
                    ? t('settings.notif_phone_required_sms_whatsapp')
                    : settings.notifySms
                    ? t('settings.notif_phone_required_sms')
                    : t('settings.notif_phone_required_whatsapp')}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Push Notifications (Service Worker) */}
        <div>
          <h3 className="text-sm font-medium text-gray-600 mb-3">{t('settings.notif_push_section')}</h3>
          <PushNotificationToggle />
        </div>

        <div className="flex items-center gap-3 pt-4">
          <button
            onClick={handleSave}
            disabled={updateMutation.isPending}
            className="inline-flex items-center px-5 py-2.5 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-xl hover:opacity-90 disabled:opacity-50 transition-opacity"
          >
            {updateMutation.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {t('settings.save_btn')}
          </button>
          {updateMutation.isSuccess && (
            <span className="inline-flex items-center text-sm text-green-600">
              <Check className="h-4 w-4 mr-1" />
              {t('settings.saved')}
            </span>
          )}
          {updateMutation.isError && (
            <span className="inline-flex items-center text-sm text-red-500">
              <AlertCircle className="h-4 w-4 mr-1" />
              {t('settings.save_error')}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function DisplaySettings() {
  const { t } = useTranslation();
  const { mapStyle, setMapStyle, theme, setTheme } = useSettingsStore();

  const getThemeIcon = (iconType: 'sun' | 'moon' | 'monitor') => {
    switch (iconType) {
      case 'sun': return Sun;
      case 'moon': return Moon;
      case 'monitor': return Monitor;
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">{t('settings.section_display')}</h2>
        <p className="text-sm text-gray-500">{t('settings.display_subtitle')}</p>
      </div>

      <div className="space-y-6">
        {/* Theme Selection */}
        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-purple-500/20">
              <Palette className="h-5 w-5 text-purple-400" />
            </div>
            <div>
              <h3 className="font-medium text-gray-900">{t('settings.display_theme_title')}</h3>
              <p className="text-xs text-gray-500">{t('settings.display_theme_subtitle')}</p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            {THEME_OPTIONS.map((option) => {
              const IconComponent = getThemeIcon(option.icon);
              return (
                <button
                  key={option.id}
                  onClick={() => setTheme(option.id)}
                  className={clsx(
                    'p-4 rounded-xl border-2 transition-all text-center',
                    theme === option.id
                      ? 'border-primary-500 bg-primary-500/10'
                      : 'border-gray-200 bg-gray-50 hover:border-gray-300 hover:bg-gray-100/50'
                  )}
                >
                  <div className="flex flex-col items-center gap-2">
                    <IconComponent className={clsx(
                      'h-6 w-6',
                      theme === option.id ? 'text-primary-600' : 'text-gray-500'
                    )} />
                    <span className={clsx(
                      'font-medium text-sm',
                      theme === option.id ? 'text-primary-600' : 'text-gray-900'
                    )}>
                      {option.name}
                    </span>
                    {theme === option.id && (
                      <Check className="h-4 w-4 text-primary-600" />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Map Style */}
        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-blue-500/20">
              <Layers className="h-5 w-5 text-blue-400" />
            </div>
            <div>
              <h3 className="font-medium text-gray-900">{t('settings.display_map_title')}</h3>
              <p className="text-xs text-gray-500">{t('settings.display_map_subtitle')}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {MAP_STYLES.map((style) => (
              <button
                key={style.id}
                onClick={() => setMapStyle(style.id)}
                className={clsx(
                  'p-4 rounded-xl border-2 transition-all text-left',
                  mapStyle === style.id
                    ? 'border-primary-500 bg-primary-500/10'
                    : 'border-gray-200 bg-gray-50 hover:border-gray-300 hover:bg-gray-100/50'
                )}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900">{style.name}</span>
                  {mapStyle === style.id && (
                    <Check className="h-4 w-4 text-primary-600" />
                  )}
                </div>
                <p className="text-xs text-gray-500">{style.description}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Map Preview */}
        <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
          <div className="flex items-center gap-3 mb-3">
            <Map className="h-5 w-5 text-green-400" />
            <div>
              <p className="font-medium text-gray-900">{t('settings.display_preview')}</p>
              <p className="text-xs text-gray-500">{t('settings.display_preview_subtitle')}</p>
            </div>
          </div>
          <div className="p-3 bg-gray-100/50 rounded-lg">
            <div className="flex items-center gap-2">
              <span className="px-3 py-1 text-xs font-medium bg-primary-500/20 text-primary-600 rounded-full">
                {MAP_STYLES.find(s => s.id === mapStyle)?.name}
              </span>
              <span className="text-xs text-gray-400">
                {t('settings.display_auto_save')}
              </span>
            </div>
          </div>
        </div>

        {/* Info about traffic */}
        {mapStyle === 'google-traffic' && (
          <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-xl">
            <div className="flex items-start gap-3">
              <Check className="h-5 w-5 text-green-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-green-400">{t('settings.display_traffic_title')}</h4>
                <p className="text-sm text-gray-500 mt-1">
                  {t('settings.display_traffic_desc')}
                  {' '}
                  Les zones vertes indiquent un trafic fluide, les zones oranges et rouges indiquent des ralentissements.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Info about satellite */}
        {(mapStyle === 'google-satellite' || mapStyle === 'google-hybrid') && (
          <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl">
            <div className="flex items-start gap-3">
              <Globe className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-blue-400">{t('settings.display_satellite_title')}</h4>
                <p className="text-sm text-gray-500 mt-1">
                  {mapStyle === 'google-hybrid'
                    ? t('settings.display_satellite_hybrid_help')
                    : t('settings.display_satellite_help')}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SecuritySettings() {
  const { t } = useTranslation();
  const { user, setUser } = useAuthStore();
  const queryClient = useQueryClient();
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwords, setPasswords] = useState({
    current: '',
    new: '',
    confirm: '',
  });

  // 2FA states
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [show2FADisable, setShow2FADisable] = useState(false);
  const [totpSecret, setTotpSecret] = useState('');
  const [totpQrCode, setTotpQrCode] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [secretCopied, setSecretCopied] = useState(false);

  // 2FA method selection
  const [duoAvailable, setDuoAvailable] = useState(false);
  const [switchingMethod, setSwitchingMethod] = useState(false);

  useEffect(() => {
    // Check available 2FA methods
    authApi.get2FAMethods().then((res) => {
      setDuoAvailable(res.data.duoAvailable);
    }).catch(() => {});
  }, []);

  const changePasswordMutation = useMutation({
    mutationFn: (data: { currentPassword: string; newPassword: string }) =>
      authApi.changePassword(data),
    onSuccess: () => {
      setShowPasswordForm(false);
      setPasswords({ current: '', new: '', confirm: '' });
    },
  });

  // 2FA mutations
  const setup2FAMutation = useMutation({
    mutationFn: () => authApi.setup2FA(),
    onSuccess: (response) => {
      setTotpSecret(response.data.secret);
      setTotpQrCode(response.data.qrCode);
    },
  });

  const enable2FAMutation = useMutation({
    mutationFn: (data: { secret: string; code: string }) =>
      authApi.enable2FA(data.secret, data.code),
    onSuccess: () => {
      setShow2FASetup(false);
      setVerificationCode('');
      setTotpSecret('');
      setTotpQrCode('');
      if (user) {
        setUser({ ...user, twoFactorEnabled: true });
      }
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
  });

  const disable2FAMutation = useMutation({
    mutationFn: (code: string) => authApi.disable2FA(code),
    onSuccess: () => {
      setShow2FADisable(false);
      setDisableCode('');
      if (user) {
        setUser({ ...user, twoFactorEnabled: false });
      }
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
  });

  const enableDuoMutation = useMutation({
    mutationFn: () => authApi.enableDuo(),
    onSuccess: () => {
      setSwitchingMethod(false);
      if (user) {
        setUser({ ...user, twoFactorEnabled: true, twoFactorMethod: 'DUO' });
      }
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
  });

  const switchToTotpMutation = useMutation({
    mutationFn: () => authApi.setup2FA(),
    onSuccess: (response) => {
      // Start TOTP setup flow to switch from Duo to TOTP
      setTotpSecret(response.data.secret);
      setTotpQrCode(response.data.qrCode);
      setShow2FASetup(true);
      setSwitchingMethod(false);
    },
  });

  const logoutAllMutation = useMutation({
    mutationFn: () => authApi.logoutAll(),
  });

  const handleChangePassword = () => {
    if (passwords.new !== passwords.confirm) {
      return;
    }
    if (passwords.new.length < 8) {
      return;
    }
    changePasswordMutation.mutate({
      currentPassword: passwords.current,
      newPassword: passwords.new,
    });
  };

  const handleStart2FASetup = () => {
    setShow2FASetup(true);
    setup2FAMutation.mutate();
  };

  const handleEnable2FA = () => {
    if (verificationCode.length !== 6) return;
    enable2FAMutation.mutate({ secret: totpSecret, code: verificationCode });
  };

  const handleDisable2FA = () => {
    if (disableCode.length !== 6) return;
    disable2FAMutation.mutate(disableCode);
  };

  const handleCopySecret = () => {
    navigator.clipboard.writeText(totpSecret);
    setSecretCopied(true);
    setTimeout(() => setSecretCopied(false), 2000);
  };

  const passwordsMatch = passwords.new === passwords.confirm;
  const passwordLongEnough = passwords.new.length >= 8;
  const canSubmit = passwords.current && passwordsMatch && passwordLongEnough;

  const is2FAEnabled = user?.twoFactorEnabled ?? false;
  const currentMethod = user?.twoFactorMethod || (is2FAEnabled ? 'TOTP' : null);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">{t('settings.section_security')}</h2>
        <p className="text-sm text-gray-500">{t('settings.security_subtitle')}</p>
      </div>

      <div className="space-y-4">
        {/* Password */}
        <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary-500/20">
                <Shield className="h-5 w-5 text-primary-600" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900">{t('settings.password_section')}</h3>
                <p className="text-xs text-gray-500">{t('settings.password_last_change')}</p>
              </div>
            </div>
            <button
              onClick={() => setShowPasswordForm(!showPasswordForm)}
              className="text-primary-600 hover:text-primary-700 text-sm font-medium"
            >
              {showPasswordForm ? t('settings.cancel') : t('settings.edit')}
            </button>
          </div>

          {showPasswordForm && (
            <div className="mt-4 pt-4 border-t border-gray-200 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-2">
                  {t('settings.password_current')}
                </label>
                <div className="relative">
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={passwords.current}
                    onChange={(e) => setPasswords({ ...passwords, current: e.target.value })}
                    className="w-full px-4 py-2.5 pr-10 bg-gray-100 border border-gray-300 rounded-xl text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-600"
                  >
                    {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-2">
                  {t('settings.password_new')}
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={passwords.new}
                    onChange={(e) => setPasswords({ ...passwords, new: e.target.value })}
                    className="w-full px-4 py-2.5 pr-10 bg-gray-100 border border-gray-300 rounded-xl text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-600"
                  >
                    {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {passwords.new && !passwordLongEnough && (
                  <p className="mt-1 text-xs text-amber-400">{t('settings.password_min_chars')}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-2">
                  {t('settings.password_confirm')}
                </label>
                <input
                  type="password"
                  value={passwords.confirm}
                  onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })}
                  className={clsx(
                    'w-full px-4 py-2.5 bg-gray-100 border rounded-xl text-gray-900 focus:ring-1',
                    passwords.confirm && !passwordsMatch
                      ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                      : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500/20'
                  )}
                />
                {passwords.confirm && !passwordsMatch && (
                  <p className="mt-1 text-xs text-red-400">{t('settings.password_mismatch')}</p>
                )}
              </div>

              {changePasswordMutation.isError && (
                <div className="flex items-center gap-2 text-sm text-red-400 p-3 bg-red-500/10 rounded-lg">
                  <AlertCircle className="h-4 w-4" />
                  {t('settings.password_current_invalid')}
                </div>
              )}

              {changePasswordMutation.isSuccess && (
                <div className="flex items-center gap-2 text-sm text-green-400 p-3 bg-green-500/10 rounded-lg">
                  <Check className="h-4 w-4" />
                  {t('settings.password_changed')}
                </div>
              )}

              <button
                onClick={handleChangePassword}
                disabled={!canSubmit || changePasswordMutation.isPending}
                className="inline-flex items-center px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-xl hover:opacity-90 disabled:opacity-50 transition-opacity"
              >
                {changePasswordMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                {t('settings.password_update_btn')}
              </button>
            </div>
          )}
        </div>

        {/* 2FA */}
        <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={clsx('p-2 rounded-lg', is2FAEnabled ? 'bg-green-500/20' : 'bg-purple-500/20')}>
                {is2FAEnabled ? (
                  <ShieldCheck className="h-5 w-5 text-green-400" />
                ) : (
                  <Smartphone className="h-5 w-5 text-purple-400" />
                )}
              </div>
              <div>
                <h3 className="font-medium text-gray-900">{t('settings.tfa_title')}</h3>
                <p className="text-xs text-gray-500">
                  {is2FAEnabled
                    ? t('settings.tfa_protected_with_method', {
                        methodSuffix:
                          currentMethod === 'DUO'
                            ? ' (Duo Security)'
                            : currentMethod === 'TOTP'
                            ? ' (TOTP)'
                            : '',
                      })
                    : t('settings.tfa_extra_layer')}
                </p>
              </div>
            </div>
            {is2FAEnabled ? (
              <span className="px-3 py-1 text-xs font-medium bg-green-500/20 text-green-400 rounded-full flex items-center gap-1">
                <Check className="h-3 w-3" />
                {t('settings.active_short')}
              </span>
            ) : !duoAvailable ? (
              <button
                onClick={handleStart2FASetup}
                disabled={setup2FAMutation.isPending}
                className="text-primary-600 hover:text-primary-700 text-sm font-medium"
              >
                {setup2FAMutation.isPending ? t('settings.loading_short') : t('settings.configure_short')}
              </button>
            ) : null}
          </div>

          {/* Method Selector - when Duo is available and 2FA is NOT yet enabled */}
          {!is2FAEnabled && duoAvailable && !show2FASetup && (
            <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
              <p className="text-sm text-gray-600 font-medium">{t('settings.tfa_choose_method')}</p>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={handleStart2FASetup}
                  disabled={setup2FAMutation.isPending || enableDuoMutation.isPending}
                  className="p-4 rounded-xl border-2 border-gray-200 bg-white hover:border-primary-300 hover:bg-primary-50/50 transition-all text-left"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Smartphone className="h-4 w-4 text-primary-600" />
                    <span className="font-medium text-sm text-gray-900">TOTP</span>
                  </div>
                  <p className="text-xs text-gray-500">Google Authenticator, Authy...</p>
                </button>

                <button
                  type="button"
                  onClick={() => enableDuoMutation.mutate()}
                  disabled={setup2FAMutation.isPending || enableDuoMutation.isPending}
                  className="p-4 rounded-xl border-2 border-gray-200 bg-white hover:border-primary-300 hover:bg-primary-50/50 transition-all text-left"
                >
                  {enableDuoMutation.isPending ? (
                    <div className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin text-primary-600" />
                      <span className="text-sm text-gray-500">{t('settings.activating')}</span>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 mb-1">
                        <ShieldCheck className="h-4 w-4 text-primary-600" />
                        <span className="font-medium text-sm text-gray-900">Duo Security</span>
                      </div>
                      <p className="text-xs text-gray-500">Duo Universal Prompt</p>
                    </>
                  )}
                </button>
              </div>

              {enableDuoMutation.isError && (
                <div className="flex items-center gap-2 text-sm text-red-400 p-3 bg-red-500/10 rounded-lg">
                  <AlertCircle className="h-4 w-4" />
                  {t('settings.duo_activate_error')}
                </div>
              )}

              {enableDuoMutation.isSuccess && (
                <div className="flex items-center gap-2 text-sm text-green-600 p-3 bg-green-500/10 rounded-lg">
                  <Check className="h-4 w-4" />
                  {t('settings.tfa_duo_enabled')}
                </div>
              )}
            </div>
          )}

          {/* 2FA TOTP Setup Form */}
          {show2FASetup && !is2FAEnabled && (
            <div className="mt-4 pt-4 border-t border-gray-200 space-y-4">
              {setup2FAMutation.isPending ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
                </div>
              ) : (
                <>
                  <div className="p-4 bg-gray-100/50 rounded-xl">
                    <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                      <QrCode className="h-4 w-4 text-primary-600" />
                      {t('settings.tfa_step1_scan')}
                    </h4>
                    <p className="text-xs text-gray-500 mb-4">
                      {t('settings.tfa_step1_scan_desc')}
                    </p>
                    {totpQrCode && (
                      <div className="flex justify-center mb-4">
                        <div className="p-3 bg-white rounded-xl">
                          <img src={totpQrCode} alt="QR Code 2FA" className="w-48 h-48" />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="p-4 bg-gray-100/50 rounded-xl">
                    <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                      <Key className="h-4 w-4 text-primary-600" />
                      {t('settings.tfa_or_manual_key')}
                    </h4>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 p-3 bg-white rounded-lg text-sm text-primary-600 font-mono break-all">
                        {totpSecret}
                      </code>
                      <button
                        onClick={handleCopySecret}
                        className="p-2 text-gray-500 hover:text-primary-600 transition-colors"
                        title={t('common.copy')}
                      >
                        {secretCopied ? <Check className="h-5 w-5 text-green-400" /> : <Copy className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>

                  <div className="p-4 bg-gray-100/50 rounded-xl">
                    <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                      <Shield className="h-4 w-4 text-primary-600" />
                      {t('settings.tfa_step2_verify')}
                    </h4>
                    <p className="text-xs text-gray-500 mb-3">
                      {t('settings.tfa_step2_help')}
                    </p>
                    <div className="flex items-center gap-3">
                      <input
                        type="text"
                        value={verificationCode}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                          setVerificationCode(value);
                        }}
                        placeholder="000000"
                        maxLength={6}
                        className="w-40 px-4 py-2.5 bg-white border border-gray-300 rounded-xl text-gray-900 text-center text-lg font-mono tracking-widest focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                      />
                      <button
                        onClick={handleEnable2FA}
                        disabled={verificationCode.length !== 6 || enable2FAMutation.isPending}
                        className="inline-flex items-center px-4 py-2.5 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-xl hover:opacity-90 disabled:opacity-50 transition-opacity"
                      >
                        {enable2FAMutation.isPending ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <ShieldCheck className="h-4 w-4 mr-2" />
                        )}
                        {t('settings.tfa_enable_btn')}
                      </button>
                    </div>

                    {enable2FAMutation.isError && (
                      <div className="mt-3 flex items-center gap-2 text-sm text-red-400 p-3 bg-red-500/10 rounded-lg">
                        <AlertCircle className="h-4 w-4" />
                        {t('settings.tfa_invalid_code')}
                      </div>
                    )}
                  </div>

                  <button
                    onClick={() => {
                      setShow2FASetup(false);
                      setTotpSecret('');
                      setTotpQrCode('');
                      setVerificationCode('');
                    }}
                    className="text-gray-500 hover:text-gray-600 text-sm"
                  >
                    {t('settings.cancel')}
                  </button>
                </>
              )}
            </div>
          )}

          {/* 2FA Method Selector - when 2FA is already enabled and Duo is available */}
          {is2FAEnabled && duoAvailable && (
            <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
              <p className="text-sm text-gray-600 font-medium">{t('settings.tfa_active_method')}</p>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => {
                    if (currentMethod !== 'TOTP') {
                      setSwitchingMethod(true);
                      switchToTotpMutation.mutate();
                    }
                  }}
                  disabled={switchingMethod}
                  className={clsx(
                    'p-4 rounded-xl border-2 transition-all text-left',
                    currentMethod === 'TOTP'
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  )}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Smartphone className="h-4 w-4 text-primary-600" />
                    <span className="font-medium text-sm text-gray-900">TOTP</span>
                    {currentMethod === 'TOTP' && <Check className="h-4 w-4 text-primary-600 ml-auto" />}
                  </div>
                  <p className="text-xs text-gray-500">Google Authenticator, Authy...</p>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    if (currentMethod !== 'DUO') {
                      setSwitchingMethod(true);
                      enableDuoMutation.mutate();
                    }
                  }}
                  disabled={switchingMethod}
                  className={clsx(
                    'p-4 rounded-xl border-2 transition-all text-left',
                    currentMethod === 'DUO'
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  )}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <ShieldCheck className="h-4 w-4 text-primary-600" />
                    <span className="font-medium text-sm text-gray-900">Duo Security</span>
                    {currentMethod === 'DUO' && <Check className="h-4 w-4 text-primary-600 ml-auto" />}
                  </div>
                  <p className="text-xs text-gray-500">Duo Universal Prompt</p>
                </button>
              </div>

              {switchingMethod && (
                <div className="flex items-center gap-2 text-sm text-primary-600">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('settings.tfa_changing')}
                </div>
              )}
            </div>
          )}

          {/* 2FA Disable Form */}
          {is2FAEnabled && !show2FADisable && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <button
                onClick={() => setShow2FADisable(true)}
                className="text-red-400 hover:text-red-300 text-sm font-medium flex items-center gap-2"
              >
                <ShieldOff className="h-4 w-4" />
                {t('settings.tfa_disable_link')}
              </button>
            </div>
          )}

          {show2FADisable && (
            <div className="mt-4 pt-4 border-t border-gray-200 space-y-4">
              <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-red-400">{t('settings.warning_title')}</h4>
                    <p className="text-sm text-gray-500 mt-1">
                      {currentMethod === 'DUO'
                        ? t('settings.tfa_disable_duo_warning')
                        : t('settings.tfa_disable_totp_warning')}
                    </p>
                  </div>
                </div>
              </div>

              {currentMethod === 'DUO' ? (
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => {
                      disable2FAMutation.mutate('000000');
                    }}
                    disabled={disable2FAMutation.isPending}
                    className="inline-flex items-center px-4 py-2.5 bg-red-500 text-white font-medium rounded-xl hover:bg-red-600 disabled:opacity-50 transition-colors"
                  >
                    {disable2FAMutation.isPending ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <ShieldOff className="h-4 w-4 mr-2" />
                    )}
                    {t('settings.tfa_confirm_disable_btn')}
                  </button>
                  <button
                    onClick={() => {
                      setShow2FADisable(false);
                    }}
                    className="text-gray-500 hover:text-gray-600 text-sm"
                  >
                    {t('settings.cancel')}
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <input
                    type="text"
                    value={disableCode}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                      setDisableCode(value);
                    }}
                    placeholder="000000"
                    maxLength={6}
                    className="w-40 px-4 py-2.5 bg-white border border-gray-300 rounded-xl text-gray-900 text-center text-lg font-mono tracking-widest focus:border-red-500 focus:ring-1 focus:ring-red-500/20"
                  />
                  <button
                    onClick={handleDisable2FA}
                    disabled={disableCode.length !== 6 || disable2FAMutation.isPending}
                    className="inline-flex items-center px-4 py-2.5 bg-red-500 text-white font-medium rounded-xl hover:bg-red-600 disabled:opacity-50 transition-colors"
                  >
                    {disable2FAMutation.isPending ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <ShieldOff className="h-4 w-4 mr-2" />
                    )}
                    {t('settings.tfa_disable_btn')}
                  </button>
                  <button
                    onClick={() => {
                      setShow2FADisable(false);
                      setDisableCode('');
                    }}
                    className="text-gray-500 hover:text-gray-600 text-sm"
                  >
                    {t('settings.cancel')}
                  </button>
                </div>
              )}

              {disable2FAMutation.isError && (
                <div className="flex items-center gap-2 text-sm text-red-400 p-3 bg-red-500/10 rounded-lg">
                  <AlertCircle className="h-4 w-4" />
                  {currentMethod === 'DUO' ? t('settings.tfa_disable_error') : t('settings.tfa_invalid_code_short')}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Passkeys */}
        <PasskeysSettings />

        {/* Sessions */}
        <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/20">
                <Globe className="h-5 w-5 text-green-400" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900">{t('settings.sessions_section')}</h3>
                <p className="text-xs text-gray-500">{t('settings.sessions_subtitle')}</p>
              </div>
            </div>
            <button
              onClick={() => logoutAllMutation.mutate()}
              disabled={logoutAllMutation.isPending}
              className="text-red-400 hover:text-red-300 text-sm font-medium flex items-center gap-1"
            >
              {logoutAllMutation.isPending && <Loader2 className="h-3 w-3 animate-spin" />}
              {t('settings.sessions_logout_all')}
            </button>
          </div>
          {logoutAllMutation.isSuccess && (
            <div className="mt-3 flex items-center gap-2 text-sm text-green-400 p-3 bg-green-500/10 rounded-lg">
              <Check className="h-4 w-4" />
              {t('settings.sessions_logged_out')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Passkeys Settings
function PasskeysSettings() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [showRegister, setShowRegister] = useState(false);
  const [friendlyName, setFriendlyName] = useState('');
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registerError, setRegisterError] = useState('');
  const [registerSuccess, setRegisterSuccess] = useState(false);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');

  const supportsPasskey = useMemo(() => browserSupportsWebAuthn(), []);

  const { data: passkeys = [], isLoading } = useQuery({
    queryKey: ['passkeys'],
    queryFn: async () => {
      const res = await authApi.listPasskeys();
      return res.data as { id: string; name: string; deviceType: string; lastUsedAt: string | null; createdAt: string }[];
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => authApi.deletePasskey(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['passkeys'] }),
  });

  const renameMutation = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => authApi.renamePasskey(id, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['passkeys'] });
      setRenamingId(null);
      setRenameValue('');
    },
  });

  const handleRegister = async () => {
    try {
      setRegisterLoading(true);
      setRegisterError('');
      setRegisterSuccess(false);

      const { data: beginData } = await authApi.passkeyRegisterBegin();
      const attestation = await registerPasskey(beginData.options);
      await authApi.passkeyRegisterFinish(beginData.challengeToken, attestation, friendlyName || undefined);

      setRegisterSuccess(true);
      setFriendlyName('');
      setShowRegister(false);
      queryClient.invalidateQueries({ queryKey: ['passkeys'] });
    } catch (err: unknown) {
      const error = err as { name?: string; response?: { data?: { error?: string } } };
      if (error.name === 'AbortError' || error.name === 'NotAllowedError') return;
      setRegisterError(error.response?.data?.error || t('settings.passkey_register_error'));
    } finally {
      setRegisterLoading(false);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return t('settings.passkey_never');
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!supportsPasskey) return null;

  return (
    <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-indigo-500/20">
            <Fingerprint className="h-5 w-5 text-indigo-500" />
          </div>
          <div>
            <h3 className="font-medium text-gray-900">{t('settings.passkey_section_title')}</h3>
            <p className="text-xs text-gray-500">
              {passkeys.length > 0
                ? t('settings.passkey_count', { count: passkeys.length })
                : t('settings.passkey_subtitle')}
            </p>
          </div>
        </div>
        <button
          onClick={() => { setShowRegister(!showRegister); setRegisterError(''); setRegisterSuccess(false); }}
          className="text-primary-600 hover:text-primary-700 text-sm font-medium"
        >
          {showRegister ? t('settings.cancel') : t('settings.passkey_add')}
        </button>
      </div>

      {registerSuccess && (
        <div className="mt-3 flex items-center gap-2 text-sm text-green-600 p-3 bg-green-500/10 rounded-lg">
          <Check className="h-4 w-4" />
          {t('settings.passkey_registered')}
        </div>
      )}

      {showRegister && (
        <div className="mt-4 pt-4 border-t border-gray-200 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2">
              {t('settings.passkey_name_label')}
            </label>
            <input
              type="text"
              value={friendlyName}
              onChange={(e) => setFriendlyName(e.target.value)}
              placeholder={t('settings.passkey_name_placeholder')}
              className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-xl text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
            />
          </div>

          {registerError && (
            <div className="flex items-center gap-2 text-sm text-red-400 p-3 bg-red-500/10 rounded-lg">
              <AlertCircle className="h-4 w-4" />
              {registerError}
            </div>
          )}

          <button
            onClick={handleRegister}
            disabled={registerLoading}
            className="inline-flex items-center px-4 py-2.5 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-xl hover:opacity-90 disabled:opacity-50 transition-opacity"
          >
            {registerLoading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Fingerprint className="h-4 w-4 mr-2" />
            )}
            {t('settings.passkey_register_btn')}
          </button>
        </div>
      )}

      {/* Passkeys list */}
      {passkeys.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-200 space-y-2">
          {isLoading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
            </div>
          ) : (
            passkeys.map((pk) => (
              <div key={pk.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-100">
                <div className="flex items-center gap-3 min-w-0">
                  <Fingerprint className="h-4 w-4 text-indigo-500 flex-shrink-0" />
                  <div className="min-w-0">
                    {renamingId === pk.id ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={renameValue}
                          onChange={(e) => setRenameValue(e.target.value)}
                          className="px-2 py-1 text-sm bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && renameValue.trim()) {
                              renameMutation.mutate({ id: pk.id, name: renameValue.trim() });
                            }
                            if (e.key === 'Escape') {
                              setRenamingId(null);
                            }
                          }}
                        />
                        <button
                          onClick={() => renameValue.trim() && renameMutation.mutate({ id: pk.id, name: renameValue.trim() })}
                          disabled={renameMutation.isPending}
                          className="text-primary-600 hover:text-primary-700"
                        >
                          <Check className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <p className="text-sm font-medium text-gray-900 truncate">{pk.name}</p>
                        <p className="text-xs text-gray-400">
                          {pk.lastUsedAt
                            ? t('settings.passkey_used_at', { date: formatDate(pk.lastUsedAt) })
                            : t('settings.passkey_created_at', { date: formatDate(pk.createdAt) })}
                        </p>
                      </>
                    )}
                  </div>
                </div>

                {renamingId !== pk.id && (
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => { setRenamingId(pk.id); setRenameValue(pk.name); }}
                      className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors"
                      title={t('settings.passkey_rename')}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => deleteMutation.mutate(pk.id)}
                      disabled={deleteMutation.isPending}
                      className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
                      title={t('settings.passkey_delete')}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// App Settings - PWA Installation and app info
function AppSettings() {
  const { t } = useTranslation();
  const { isInstalled, isStandalone, isIOS } = useInstallPrompt();
  const {
    batterySaverMode,
    refreshInterval,
    reduceAnimations,
    setBatterySaverMode,
    setRefreshInterval,
    setReduceAnimations,
  } = useSettingsStore();

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">{t('settings.section_app')}</h2>
        <p className="text-sm text-gray-500">{t('settings.app_section_subtitle')}</p>
      </div>

      <div className="space-y-6">
        {/* Install Section */}
        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-cyan-500/20">
              <Smartphone className="h-5 w-5 text-cyan-500" />
            </div>
            <div>
              <h3 className="font-medium text-gray-900">{t('settings.app_install_section')}</h3>
              <p className="text-xs text-gray-500">
                {isInstalled || isStandalone
                  ? t('settings.app_installed_subtitle')
                  : t('settings.app_install_subtitle')}
              </p>
            </div>
          </div>

          {isInstalled || isStandalone ? (
            <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
              <div className="flex items-center gap-3">
                <Check className="w-5 h-5 text-green-600" />
                <div>
                  <p className="font-medium text-green-900">{t('settings.app_installed_title')}</p>
                  <p className="text-sm text-green-700">
                    {t('settings.app_on_home_screen')}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <InstallPrompt variant="inline" forceShow />
          )}
        </div>

        {/* App Info */}
        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-blue-500/20">
              <Globe className="h-5 w-5 text-blue-500" />
            </div>
            <div>
              <h3 className="font-medium text-gray-900">{t('settings.app_info_section')}</h3>
              <p className="text-xs text-gray-500">{t('settings.app_details_subtitle')}</p>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">{t('settings.app_version_label')}</span>
              <span className="text-sm font-medium text-gray-900">1.0.0</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">{t('settings.app_type_label')}</span>
              <span className="text-sm font-medium text-gray-900">
                {isStandalone ? t('settings.app_installed_text') : t('settings.app_web_text')}
              </span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">{t('settings.app_platform_label')}</span>
              <span className="text-sm font-medium text-gray-900">
                {isIOS ? 'iOS' : /Android/.test(navigator.userAgent) ? 'Android' : 'Desktop'}
              </span>
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-gray-600">{t('settings.app_offline_section')}</span>
              <span className="text-sm font-medium text-green-600">{t('settings.app_enabled')}</span>
            </div>
          </div>
        </div>

        {/* Driver Mode */}
        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-cyan-500/20">
              <Car className="h-5 w-5 text-cyan-500" />
            </div>
            <div>
              <h3 className="font-medium text-gray-900">{t('settings.app_driver_mode')}</h3>
              <p className="text-xs text-gray-500">{t('settings.app_share_position')}</p>
            </div>
          </div>

          <DriverModeToggle variant="full" />
        </div>

        {/* Battery Saver Mode */}
        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-green-500/20">
              <Battery className="h-5 w-5 text-green-500" />
            </div>
            <div>
              <h3 className="font-medium text-gray-900">{t('settings.app_battery_section')}</h3>
              <p className="text-xs text-gray-500">{t('settings.app_battery_subtitle')}</p>
            </div>
          </div>

          <div className="space-y-4">
            {/* Battery Saver Toggle */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
              <div>
                <p className="text-sm font-medium text-gray-900">{t('settings.app_economy_mode')}</p>
                <p className="text-xs text-gray-500">
                  {t('settings.app_economy_desc')}
                </p>
              </div>
              <button
                onClick={() => setBatterySaverMode(!batterySaverMode)}
                className={clsx(
                  'relative inline-flex h-6 w-11 min-w-[44px] flex-shrink-0 items-center rounded-full transition-colors',
                  batterySaverMode ? 'bg-green-500' : 'bg-gray-300'
                )}
              >
                <span
                  className={clsx(
                    'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
                    batterySaverMode ? 'translate-x-6' : 'translate-x-1'
                  )}
                />
              </button>
            </div>

            {/* Refresh Interval */}
            <div>
              <p className="text-sm font-medium text-gray-900 mb-2">
                {t('settings.app_refresh_freq')}
              </p>
              <select
                value={refreshInterval}
                onChange={(e) => setRefreshInterval(Number(e.target.value))}
                className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
              >
                <option value={10}>{t('settings.app_refresh_10s')}</option>
                <option value={30}>{t('settings.app_refresh_30s')}</option>
                <option value={60}>{t('settings.app_refresh_60s')}</option>
                <option value={120}>{t('settings.app_refresh_120s')}</option>
                <option value={300}>{t('settings.app_refresh_300s')}</option>
              </select>
              {batterySaverMode && refreshInterval < 60 && (
                <p className="mt-1 text-xs text-amber-600">
                  {t('settings.app_economy_min')}
                </p>
              )}
            </div>

            {/* Reduce Animations */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
              <div>
                <p className="text-sm font-medium text-gray-900">{t('settings.app_reduce_animations')}</p>
                <p className="text-xs text-gray-500">
                  {t('settings.app_reduce_animations_desc')}
                </p>
              </div>
              <button
                onClick={() => setReduceAnimations(!reduceAnimations)}
                className={clsx(
                  'relative inline-flex h-6 w-11 min-w-[44px] flex-shrink-0 items-center rounded-full transition-colors',
                  reduceAnimations ? 'bg-green-500' : 'bg-gray-300'
                )}
              >
                <span
                  className={clsx(
                    'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
                    reduceAnimations ? 'translate-x-6' : 'translate-x-1'
                  )}
                />
              </button>
            </div>
          </div>
        </div>

        {/* Cache & Storage */}
        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-purple-500/20">
              <Layers className="h-5 w-5 text-purple-500" />
            </div>
            <div>
              <h3 className="font-medium text-gray-900">{t('settings.app_storage_section')}</h3>
              <p className="text-xs text-gray-500">{t('settings.app_offline_subtitle')}</p>
            </div>
          </div>

          <p className="text-sm text-gray-600">
            {t('settings.app_offline_help')}
          </p>
        </div>
      </div>
    </div>
  );
}

interface TenantFormData {
  companyName: string;
  address: string;
  addressLine2: string;
  city: string;
  postalCode: string;
  country: string;
  phone: string;
  email: string;
  vatNumber: string;
  siret: string;
  website: string;
  billingEmail: string;
  timezone: string;
  logo: string;
  primaryColor: string;
}

const emptyForm: TenantFormData = {
  companyName: '', address: '', addressLine2: '', city: '', postalCode: '',
  country: '', phone: '', email: '', vatNumber: '', siret: '', website: '',
  billingEmail: '', timezone: '', logo: '', primaryColor: '',
};

/** Indexed by ISO weekday - 1, so index 0 is Monday. */
/**
 * Company-wide WORKING hours. Privacy is their complement — see
 * WorkScheduleEditor for why the form asks about work rather than about
 * privacy.
 */
function TenantPrivacyDefaults() {
  const { user } = useAuthStore();
  const { selectedTenant } = useTenantContextStore();
  const queryClient = useQueryClient();
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN';
  const tenantKey = selectedTenant?.id || 'default';

  const { data } = useQuery({
    queryKey: ['tenant-privacy-defaults', tenantKey],
    queryFn: () => tenantSettingsApi.getPrivacyDefaults().then((r) => r.data),
    enabled: isAdmin,
  });

  const [enabled, setEnabled] = useState(false);
  const [schedule, setSchedule] = useState<WorkSchedule>(DEFAULT_WORK_SCHEDULE);

  useEffect(() => {
    if (data) {
      setEnabled(data.enabled);
      setSchedule((data.workSchedule as WorkSchedule) ?? DEFAULT_WORK_SCHEDULE);
    }
  }, [data]);

  const mutation = useMutation({
    mutationFn: () =>
      tenantSettingsApi.updatePrivacyDefaults({
        enabled,
        workSchedule: enabled ? schedule : null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-privacy-defaults', tenantKey] });
      // Inheriting vehicles now report a different effective schedule.
      queryClient.invalidateQueries({ queryKey: ['devices', tenantKey] });
      queryClient.invalidateQueries({ queryKey: ['my-privacy'] });
    },
  });

  if (!isAdmin) return null;

  const invalid = enabled && !hasSomeWork(schedule);

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="flex items-center mb-1">
        <Shield className="h-5 w-5 text-gray-400 mr-2" />
        <h3 className="text-lg font-semibold text-gray-900">Vie privée des conducteurs</h3>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Renseignez les <strong>heures de travail</strong> de l'entreprise : en dehors, aucune
        position n'est enregistrée. Les données ne sont pas masquées, elles ne sont jamais
        collectées. Un véhicule peut définir ses propres horaires depuis sa fiche.
      </p>

      <label className="flex items-center gap-2 text-sm text-gray-700 mb-4">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        Activer le mode vie privée par défaut
      </label>

      {enabled && (
        <div className="space-y-4">
          <WorkScheduleEditor value={schedule} onChange={setSchedule} />

          <div className="rounded-lg bg-indigo-50 px-3 py-2 text-xs text-indigo-800">
            Résultat : {privacySummary(schedule)}.
          </div>

          {data?.timezone && (
            <p className="text-xs text-gray-500">
              Horaires exprimés dans le fuseau de l'entreprise ({data.timezone}).
            </p>
          )}
        </div>
      )}

      <div className="mt-4 flex items-center gap-3">
        <button
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending || invalid}
          className="flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors disabled:opacity-50"
        >
          {mutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : mutation.isSuccess ? (
            <Check className="h-4 w-4" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          {mutation.isSuccess ? 'Enregistré' : 'Enregistrer'}
        </button>

        {mutation.isError && (
          <span className="flex items-center gap-1 text-sm text-red-600">
            <AlertCircle className="h-4 w-4" />
            Échec de l'enregistrement
          </span>
        )}
      </div>
    </div>
  );
}

function TenantSettings() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const { selectedTenant } = useTenantContextStore();
  const queryClient = useQueryClient();
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN';
  const tenantKey = selectedTenant?.id || 'default';
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState<TenantFormData>(emptyForm);

  const [usage, setUsage] = useState<{
    devices: { used: number; limit: number };
    users: { used: number; limit: number };
    geofences: { used: number; limit: number };
    retentionDays: number;
  } | null>(null);
  const [subscriptions, setSubscriptions] = useState<{ name: string }[]>([]);
  const [usageLoading, setUsageLoading] = useState(true);

  useEffect(() => {
    setUsageLoading(true);
    const fetchData = async () => {
      try {
        const [usageRes, subRes] = await Promise.all([
          billingApi.getUsage(),
          billingApi.getSubscription(),
        ]);
        setUsage(usageRes.data);
        setSubscriptions(subRes.data?.subscriptions || []);
      } catch (error) {
        console.error('Error fetching tenant data:', error);
      } finally {
        setUsageLoading(false);
      }
    };
    fetchData();
  }, [tenantKey]);

  const { data: tenant, isLoading: tenantLoading } = useQuery({
    queryKey: ['tenant', tenantKey],
    queryFn: () => authApi.getTenant().then((r) => r.data),
    enabled: isAdmin,
  });

  useEffect(() => {
    if (tenant) {
      setFormData({
        companyName: tenant.companyName || '',
        address: tenant.address || '',
        addressLine2: tenant.addressLine2 || '',
        city: tenant.city || '',
        postalCode: tenant.postalCode || '',
        country: tenant.country || '',
        phone: tenant.phone || '',
        email: tenant.email || '',
        vatNumber: tenant.vatNumber || '',
        siret: tenant.siret || '',
        website: tenant.website || '',
        billingEmail: tenant.billingEmail || '',
        timezone: tenant.timezone || '',
        logo: tenant.logo || '',
        primaryColor: tenant.primaryColor || '',
      });
    }
  }, [tenant]);

  const updateMutation = useMutation({
    mutationFn: (data: Partial<TenantFormData>) => authApi.updateTenant(
      Object.fromEntries(Object.entries(data).map(([k, v]) => [k, v === '' ? null : v]))
    ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant', tenantKey] });
      setEditing(false);
    },
  });

  const handleSave = () => {
    updateMutation.mutate(formData);
  };

  const handleCancel = () => {
    if (tenant) {
      setFormData({
        companyName: tenant.companyName || '',
        address: tenant.address || '',
        addressLine2: tenant.addressLine2 || '',
        city: tenant.city || '',
        postalCode: tenant.postalCode || '',
        country: tenant.country || '',
        phone: tenant.phone || '',
        email: tenant.email || '',
        vatNumber: tenant.vatNumber || '',
        siret: tenant.siret || '',
        website: tenant.website || '',
        billingEmail: tenant.billingEmail || '',
        timezone: tenant.timezone || '',
        logo: tenant.logo || '',
        primaryColor: tenant.primaryColor || '',
      });
    }
    setEditing(false);
  };

  const set = (key: keyof TenantFormData) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setFormData((prev) => ({ ...prev, [key]: e.target.value }));

  const planName = subscriptions.length > 0
    ? subscriptions.map(s => s.name).join(', ')
    : t('settings.tenant_no_subscription');

  const inputCls = (disabled: boolean) => clsx(
    'w-full px-4 py-2.5 border rounded-xl text-gray-900 transition-colors',
    disabled
      ? 'bg-gray-50 border-gray-200 text-gray-500 cursor-not-allowed'
      : 'bg-white border-gray-200 placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20'
  );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">{t('settings.section_organization')}</h2>
        <p className="text-sm text-gray-500">{t('settings.tenant_subtitle')}</p>
      </div>

      <div className="space-y-6">
        {/* Usage card */}
        <div className="p-5 bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl border border-primary-500/20">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center text-white text-xl font-bold">
              {(tenant?.name || user?.tenantName)?.[0]?.toUpperCase()}
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{tenant?.name || user?.tenantName}</h3>
              <p className="text-sm text-gray-500">{planName}</p>
            </div>
          </div>
          {usageLoading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="h-6 w-6 animate-spin text-primary-500" />
            </div>
          ) : usage ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500">{t('settings.tenant_vehicles')}</p>
                <p className="text-lg font-semibold text-gray-900">
                  {usage.devices.used}{' '}
                  <span className="text-gray-400 text-sm">/ {usage.devices.limit === 0 ? '∞' : usage.devices.limit}</span>
                </p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500">{t('settings.tenant_users')}</p>
                <p className="text-lg font-semibold text-gray-900">
                  {usage.users.used}{' '}
                  <span className="text-gray-400 text-sm">/ {usage.users.limit === 0 ? '∞' : usage.users.limit}</span>
                </p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500">{t('settings.tenant_zones')}</p>
                <p className="text-lg font-semibold text-gray-900">
                  {usage.geofences.used}{' '}
                  <span className="text-gray-400 text-sm">/ {usage.geofences.limit === 0 ? '∞' : usage.geofences.limit}</span>
                </p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500">{t('settings.tenant_retention')}</p>
                <p className="text-lg font-semibold text-gray-900">{usage.retentionDays} jours</p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500 text-center py-4">{t('settings.tenant_load_error')}</p>
          )}
        </div>

        {/* Company info form */}
        {isAdmin ? (
          tenantLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-gray-600">{t('settings.tenant_company_section')}</h3>
                {!editing && (
                  <button
                    onClick={() => setEditing(true)}
                    className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                  >
                    {t('settings.edit')}
                  </button>
                )}
              </div>

              {/* Logo + brand colour — used to brand POD delivery emails (falls back to TrackFleet). */}
              <div className="rounded-lg border border-gray-200 p-4">
                <label className="block text-sm font-medium text-gray-600 mb-2">{t('settings.tenant_logo')}</label>
                <div className="flex items-center gap-3 flex-wrap">
                  {formData.logo ? (
                    <img src={formData.logo} alt="logo" className="h-12 max-w-[180px] object-contain border border-gray-200 rounded bg-white p-1" />
                  ) : (
                    <span className="text-xs text-gray-400">{t('settings.tenant_logo_none')}</span>
                  )}
                  {editing && (
                    <>
                      <label className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                        {t('settings.tenant_logo_upload')}
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/webp,image/svg+xml"
                          className="hidden"
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (!f) return;
                            const r = new FileReader();
                            r.onload = () => setFormData((p) => ({ ...p, logo: String(r.result) }));
                            r.readAsDataURL(f);
                            e.target.value = '';
                          }}
                        />
                      </label>
                      {formData.logo && (
                        <button type="button" onClick={() => setFormData((p) => ({ ...p, logo: '' }))} className="text-sm text-red-600">
                          {t('settings.tenant_logo_remove')}
                        </button>
                      )}
                      <div className="flex items-center gap-2 ml-auto">
                        <span className="text-sm text-gray-600">{t('settings.tenant_color')}</span>
                        <input
                          type="color"
                          value={/^#[0-9a-fA-F]{6}$/.test(formData.primaryColor) ? formData.primaryColor : '#1f8da1'}
                          onChange={(e) => setFormData((p) => ({ ...p, primaryColor: e.target.value }))}
                          className="h-8 w-12 rounded border border-gray-300 cursor-pointer"
                        />
                      </div>
                    </>
                  )}
                </div>
                <p className="text-xs text-gray-400 mt-2">{t('settings.tenant_logo_help')}</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">{t('settings.tenant_company_name')}</label>
                  <input type="text" value={formData.companyName} onChange={set('companyName')} disabled={!editing} placeholder={t('settings.tenant_company_placeholder')} className={inputCls(!editing)} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">{t('settings.tenant_field_email')}</label>
                  <input type="email" value={formData.email} onChange={set('email')} disabled={!editing} placeholder="contact@societe.com" className={inputCls(!editing)} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">{t('settings.field_phone')}</label>
                  <input type="tel" value={formData.phone} onChange={set('phone')} disabled={!editing} placeholder="+33 1 23 45 67 89" className={inputCls(!editing)} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">{t('settings.tenant_field_website')}</label>
                  <input type="url" value={formData.website} onChange={set('website')} disabled={!editing} placeholder="https://www.societe.com" className={inputCls(!editing)} />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-600 mb-2">{t('settings.tenant_field_address')}</label>
                  <input type="text" value={formData.address} onChange={set('address')} disabled={!editing} placeholder="12 rue de la Paix" className={inputCls(!editing)} />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-600 mb-2">{t('settings.tenant_address_line2')}</label>
                  <input type="text" value={formData.addressLine2} onChange={set('addressLine2')} disabled={!editing} placeholder={t('settings.tenant_address_line2_placeholder')} className={inputCls(!editing)} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">{t('settings.tenant_field_postal')}</label>
                  <input type="text" value={formData.postalCode} onChange={set('postalCode')} disabled={!editing} placeholder="75001" className={inputCls(!editing)} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">{t('settings.tenant_field_city')}</label>
                  <input type="text" value={formData.city} onChange={set('city')} disabled={!editing} placeholder="Paris" className={inputCls(!editing)} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">{t('settings.tenant_field_country')}</label>
                  <input type="text" value={formData.country} onChange={set('country')} disabled={!editing} placeholder="FR" maxLength={2} className={inputCls(!editing)} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">{t('settings.tenant_field_timezone')}</label>
                  <input type="text" value={formData.timezone} onChange={set('timezone')} disabled={!editing} placeholder="Europe/Paris" className={inputCls(!editing)} />
                </div>
              </div>

              <div className="flex items-center justify-between pt-2">
                <h3 className="text-sm font-medium text-gray-600">{t('settings.tenant_fiscal_section')}</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">{t('settings.tenant_field_vat')}</label>
                  <input type="text" value={formData.vatNumber} onChange={set('vatNumber')} disabled={!editing} placeholder="FR12345678901" className={inputCls(!editing)} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">{t('settings.tenant_field_siret')}</label>
                  <input type="text" value={formData.siret} onChange={set('siret')} disabled={!editing} placeholder="123 456 789 00001" className={inputCls(!editing)} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">{t('settings.tenant_field_billing_email')}</label>
                  <input type="email" value={formData.billingEmail} onChange={set('billingEmail')} disabled={!editing} placeholder="facturation@societe.com" className={inputCls(!editing)} />
                </div>
              </div>

              {editing && (
                <div className="flex items-center gap-3 pt-2">
                  <button
                    onClick={handleSave}
                    disabled={updateMutation.isPending}
                    className="inline-flex items-center px-5 py-2.5 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-xl hover:opacity-90 disabled:opacity-50 transition-opacity"
                  >
                    {updateMutation.isPending ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4 mr-2" />
                    )}
                    {t('settings.save_btn')}
                  </button>
                  <button
                    onClick={handleCancel}
                    className="px-5 py-2.5 text-gray-600 hover:text-gray-900 font-medium rounded-xl transition-colors"
                  >
                    {t('settings.cancel')}
                  </button>
                  {updateMutation.isSuccess && (
                    <span className="inline-flex items-center text-sm text-green-600">
                      <Check className="h-4 w-4 mr-1" />
                      {t('settings.saved')}
                    </span>
                  )}
                  {updateMutation.isError && (
                    <span className="inline-flex items-center text-sm text-red-500">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {t('settings.save_error')}
                    </span>
                  )}
                </div>
              )}

              {!editing && updateMutation.isSuccess && (
                <div className="flex items-center gap-2 text-sm text-green-600 p-3 bg-green-50 rounded-lg border border-green-200">
                  <Check className="h-4 w-4" />
                  {t('settings.tenant_changes_saved')}
                </div>
              )}
            </>
          )
        ) : (
          <div className="p-4 bg-amber-500/10 rounded-xl border border-amber-500/20">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-amber-400">{t('settings.tenant_access_limited')}</h3>
                <p className="text-sm text-gray-500">
                  {t('settings.tenant_access_limited_desc')}
                  {' '}
                  {t('settings.tenant_access_contact_admin')}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ToggleItem({
  icon: Icon,
  label,
  description,
  checked,
  onChange,
}: {
  icon: React.ElementType;
  label: string;
  description?: string;
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3 p-4 bg-gray-50 rounded-xl border border-gray-200">
      <div className="flex items-center gap-3 min-w-0">
        <Icon className={clsx('h-5 w-5 flex-shrink-0', checked ? 'text-primary-600' : 'text-gray-400')} />
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{label}</p>
          {description && <p className="text-xs text-gray-500">{description}</p>}
        </div>
      </div>
      <ToggleSwitch checked={checked} onChange={onChange} />
    </div>
  );
}

function ToggleSwitch({ checked, onChange }: { checked: boolean; onChange: () => void }) {
  return (
    <button
      onClick={onChange}
      className={clsx(
        'relative inline-flex h-6 w-11 min-w-[44px] flex-shrink-0 items-center rounded-full transition-colors',
        checked ? 'bg-primary-500' : 'bg-gray-300'
      )}
    >
      <span
        className={clsx(
          'inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm',
          checked ? 'translate-x-6' : 'translate-x-1'
        )}
      />
    </button>
  );
}
